import { Injectable } from '@angular/core';

/**
 * MockDataService simulates the ReactNativeWebView bridge for browser testing.
 * 
 * Purpose:
 * - Simulates user info from main app (phone, userId, token, language)
 * - Does NOT provide fake API data
 * - Services always call real backend APIs
 * 
 * Features:
 * - Auto-enabled on localhost or when ReactNativeWebView is not available
 * - Manual toggle via localStorage: 'insee_simulate_main_app'
 * - Dispatches window postMessage event like the real main app
 * 
 * Usage:
 * - Toggle via DevTools to simulate main app WebView bridge
 * - Component receives user info via window.addEventListener('message')
 * - All API calls go to real backend
 */
@Injectable({
  providedIn: 'root'
})
export class MockDataService {
  private readonly STORAGE_KEY = 'insee_simulate_main_app';
  private readonly DELAY_MS = 500; // Simulate network delay

  /**
   * Mock user info - simulates what the main app sends via ReactNativeWebView
   * This data structure matches what the real app sends via postMessage
   */
  private mockUserInfo = {
    username: '2055510515',  // Phone with "20" prefix (sample user)
    userid: 'TEST_USER_001', // User ID from main app
    lang: 'lo',              // User's preferred language
    token: 'mock_jwt_token_for_api_testing', // JWT token for API authentication
    userOtp: 'mock_otp_123456' // OTP for payment verification
  };

  constructor() {
    // Attach a lightweight devtools bridge for manual control
    this.attachDevToolsBridge();
    this.logSimulationStatus();
    // Auto-dispatch if simulation is active and no ReactNativeWebView
    if (this.isSimulationEnabled()) {
      this.simulateWebViewMessage();
    }
  }

  /**
   * Check if WebView simulation is enabled
   * Auto-enables when:
   * - Running on localhost/127.0.0.1
   * - ReactNativeWebView is not available (browser testing)
   * - localStorage flag is set to 'true'
   */
  isSimulationEnabled(): boolean {
    if (typeof window === 'undefined') return false;

    // Check if ReactNativeWebView exists (real app environment)
    const hasReactNativeWebView = !!(window as any).ReactNativeWebView;
    
    // If in real app, don't simulate
    if (hasReactNativeWebView) return false;

    // Check localStorage preference
    const storageValue = localStorage.getItem(this.STORAGE_KEY);
    if (storageValue !== null) {
      return storageValue === 'true';
    }

    // Auto-enable on localhost for development
    const hostname = window.location.hostname;
    // Treat common dev IP patterns as dev environment (e.g. local LAN or mapped dev host)
    const isDevIp = /^19\./.test(hostname) || /^192\.168\./.test(hostname) || hostname === 'localhost' || hostname === '127.0.0.1';
    return isDevIp;
  }

  /**
   * Enable WebView simulation
   * Stores preference in localStorage
   */
  enableSimulation(): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.STORAGE_KEY, 'true');
    }
  }

  /**
   * Disable WebView simulation
   * Removes preference from localStorage
   */
  disableSimulation(): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.STORAGE_KEY, 'false');
    }
  }

  /**
   * Toggle WebView simulation on/off
   * @returns New simulation state
   */
  toggleSimulation(): boolean {
    const newState = !this.isSimulationEnabled();
    if (newState) {
      this.enableSimulation();
    } else {
      this.disableSimulation();
    }
    return newState;
  }

  /**
   * Simulate the main app sending user info via postMessage
   * This dispatches a window message event that ih-home component will receive
   * 
   * Called automatically by ih-home when:
   * - ReactNativeWebView is not available
   * - Simulation is enabled
   * 
   * CRITICAL: Message structure must match what ih-home.component.ts expects:
   * { userinfo: "stringified JSON" } NOT { type: 'userinfo', payload: {...} }
   */
  simulateWebViewMessage(): void {
    if (typeof window === 'undefined') return;

    // Dispatch message event after delay (simulating async nature)
    setTimeout(() => {
      const event = new MessageEvent('message', {
        data: {
          userinfo: JSON.stringify(this.mockUserInfo) // ✅ Matches ih-home handleMessage() expectation
        },
        origin: window.location.origin
      });

      window.dispatchEvent(event);
    }, this.DELAY_MS);
  }

  /**
   * Get mock user info for display purposes
   * Useful for DevTools to show current simulated user
   */
  getMockUserInfo() {
    return { ...this.mockUserInfo };
  }

  /**
   * Update mock user info (for testing different users)
   * @param userInfo Partial user info to update
   */
  setMockUserInfo(userInfo: Partial<typeof this.mockUserInfo>): void {
    this.mockUserInfo = { ...this.mockUserInfo, ...userInfo };
    this.logUpdate();
  }

  /**
   * Check if currently running in ReactNativeWebView
   */
  isInWebView(): boolean {
    return typeof window !== 'undefined' && !!(window as any).ReactNativeWebView;
  }

  /**
   * Reinitialize simulation: clear session/cookies and resend message
   * Useful for testing without page reload
   */
  reinit(): void {
    if (typeof window === 'undefined') return;
    
    // Clear session storage
    sessionStorage.removeItem('ih_user_phone');
    sessionStorage.removeItem('ih_user_info');
    
    // Clear cookies
    const cookiesToClear = ['ih_phone', 'ih_userid', 'ih_otp', 'ih_lang'];
    cookiesToClear.forEach(name => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
    
    // Resend simulation message
    this.simulateWebViewMessage();
    
    // eslint-disable-next-line no-console
    console.info('[MockDataService] Reinitialized - message resent');
  }

  /**
   * Attach window.inseeMock helper for DevTools usage
   */
  private attachDevToolsBridge(): void {
    if (typeof window === 'undefined') return;
    const bridge = {
      enable: () => { this.enableSimulation(); this.logSimulationStatus(); return true; },
      disable: () => { this.disableSimulation(); this.logSimulationStatus(); return false; },
      toggle: () => { const state = this.toggleSimulation(); this.logSimulationStatus(); return state; },
      get: () => ({ active: this.isSimulationEnabled(), user: this.getMockUserInfo() }),
      set: (data: Partial<typeof this.mockUserInfo>) => { this.setMockUserInfo(data); return this.getMockUserInfo(); },
      send: () => { this.simulateWebViewMessage(); return 'sent'; },
      reinit: () => { this.reinit(); return 'reinitialized'; }
    } as const;
    (window as any).inseeMock = bridge;
  }

  /** Log current simulation state */
  private logSimulationStatus(): void {
    if (typeof window === 'undefined') return;
    // eslint-disable-next-line no-console
    console.info('[MockDataService] simulation:', this.isSimulationEnabled(), 'user:', this.mockUserInfo);
    if (this.isSimulationEnabled()) {
      // eslint-disable-next-line no-console
      console.info('[MockDataService] Use window.inseeMock.{enable,disable,toggle,get,set,send,reinit} in DevTools');
    }
  }

  /** Log when user info updated */
  private logUpdate(): void {
    // eslint-disable-next-line no-console
    console.info('[MockDataService] Updated mock user info:', this.mockUserInfo);
  }
}
